import React, { useState, useEffect } from 'react';
import { Card, Form, Select, DatePicker, Button, Row, Col, Radio, InputNumber, Input, Modal, Steps, message, Tag, List } from 'antd';
import { CarOutlined, EnvironmentOutlined, ClockCircleOutlined, UserOutlined, PhoneOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { RootState } from '../../store/store';
import { setFilter, fetchRoutesSuccess, createRideOrderSuccess, setCurrentOrder } from '../../store/slices/rideSlice';
import DemoBanner from '../../components/DemoBanner';

const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;

const RidePage: React.FC = () => {
  const [form] = Form.useForm();
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [serviceType, setServiceType] = useState<'carpool' | 'charter'>('carpool');
  const [orderData, setOrderData] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { availableRoutes, currentOrder, loading } = useSelector((state: RootState) => state.ride);

  // シミュレート城市和路线数据
  const cities = [
    { value: 'tokyo', label: '東京' },
    { value: 'osaka', label: '大阪' },
    { value: 'nagoya', label: '名古屋' },
    { value: 'fukuoka', label: '福岡' },
  ];

  const mockRoutes = [
    {
      id: '1',
      fromCity: 'tokyo',
      toCity: 'osaka',
      carpoolPrice: 45,
      charterPrice: 280,
      duration: 90,
      distance: 120,
      availableSeats: 3,
      pickupPoints: ['東京駅', '品川駅', '羽田空港'],
      dropoffPoints: ['大阪駅', '新大阪駅', '関西空港'],
    },
    {
      id: '2',
      fromCity: 'tokyo',
      toCity: 'nagoya',
      carpoolPrice: 65,
      charterPrice: 380,
      duration: 150,
      distance: 280,
      availableSeats: 2,
      pickupPoints: ['北京南站', '北京西站'],
      dropoffPoints: ['石家庄站', '正定机场'],
    },
  ];

  useEffect(() => {
    dispatch(fetchRoutesSuccess(mockRoutes));
  }, [dispatch]);

  const handleSearch = (values: any) => {
    const filteredRoutes = mockRoutes.filter(route => 
      route.fromCity === values.fromCity && route.toCity === values.toCity
    );
    dispatch(fetchRoutesSuccess(filteredRoutes));
    dispatch(setFilter(values));
    message.success(`找到 ${filteredRoutes.length} 条路线`);
  };

  const handleBookRide = (route: any, formData: any) => {
    const orderInfo = {
      ...formData,
      route,
      serviceType,
      totalAmount: serviceType === 'carpool' 
        ? route.carpoolPrice * formData.passengerCount 
        : route.charterPrice * (formData.priceMultiplier || 1),
    };
    setOrderData(orderInfo);
    setConfirmModalVisible(true);
  };

  const handleConfirmOrder = () => {
    if (!orderData) return;

    const newOrder = {
      id: Date.now().toString(),
      serviceType,
      passengerId: 'current-user',
      pickupLocation: {
        city: orderData.fromCity,
        address: orderData.pickupPoint,
        coordinates: [116.4074, 39.9042] as [number, number],
      },
      dropoffLocation: {
        city: orderData.toCity,
        address: orderData.dropoffPoint,
        coordinates: [117.2008, 39.0842] as [number, number],
      },
      scheduledTime: orderData.scheduledTime,
      passengerCount: orderData.passengerCount,
      vehicleType: orderData.vehicleType,
      priceMultiplier: orderData.priceMultiplier,
      note: orderData.note,
      totalAmount: orderData.totalAmount,
      status: 'pending' as const,
      paymentStatus: 'unpaid' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    dispatch(createRideOrderSuccess(newOrder));
    setConfirmModalVisible(false);
    message.success('注文作成成功！正在为您分配ドライバー...');
    navigate('/ride-orders');
  };

  const RouteCard = ({ route }: { route: any }) => (
    <Card className="route-card" style={{ marginBottom: 16 }}>
      <Form
        onFinish={(values) => handleBookRide(route, values)}
        initialValues={{
          fromCity: route.fromCity,
          toCity: route.toCity,
          passengerCount: 1,
          priceMultiplier: 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
              <EnvironmentOutlined style={{ marginRight: 8 }} />
              {cities.find(c => c.value === route.fromCity)?.label} → {cities.find(c => c.value === route.toCity)?.label}
            </h3>
            <p style={{ margin: '4px 0', color: '#666' }}>
              <ClockCircleOutlined style={{ marginRight: 4 }} />
              约 {route.duration} 分钟 · {route.distance} 公里
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
              ¥{serviceType === 'carpool' ? route.carpoolPrice : route.charterPrice}
              {serviceType === 'carpool' && '/人'}
            </div>
            {serviceType === 'carpool' && (
              <div style={{ fontSize: 12, color: '#666' }}>
                剩余座位: {route.availableSeats}
              </div>
            )}
          </div>
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="pickupPoint" label="上车地点" rules={[{ required: true }]}>
              <Select placeholder="选择上车地点">
                {route.pickupPoints.map((point: string) => (
                  <Option key={point} value={point}>{point}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="dropoffPoint" label="下车地点" rules={[{ required: true }]}>
              <Select placeholder="选择下车地点">
                {route.dropoffPoints.map((point: string) => (
                  <Option key={point} value={point}>{point}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="scheduledTime" label="出发時間" rules={[{ required: true }]}>
              <DatePicker 
                showTime 
                placeholder="选择出发時間"
                style={{ width: '100%' }}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                disabledTime={(current) => {
                  if (current && current.isSame(dayjs(), 'day')) {
                    return {
                      disabledHours: () => {
                        const hours = [];
                        const now = dayjs();
                        for (let i = 0; i < now.hour() + (serviceType === 'carpool' ? 1 : 0); i++) {
                          hours.push(i);
                        }
                        return hours;
                      },
                    };
                  }
                  return {};
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={serviceType === 'charter' ? 6 : 12}>
            <Form.Item name="passengerCount" label="乘客人数">
              <InputNumber min={1} max={serviceType === 'carpool' ? route.availableSeats : 7} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {serviceType === 'charter' && (
            <>
              <Col xs={24} sm={6}>
                <Form.Item name="vehicleType" label="车型选择">
                  <Select placeholder="选择车型">
                    <Option value="business5">商务5座</Option>
                    <Option value="business7">商务7座</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name="priceMultiplier" label="价格倍数">
                  <Select placeholder="选择倍数">
                    <Option value={1}>标准价格 (1x)</Option>
                    <Option value={1.2}>优先接单 (1.2x)</Option>
                    <Option value={1.5}>急需用车 (1.5x)</Option>
                  </Select>
                </Form.Item>
              </Col>
            </>
          )}
          <Col xs={24} sm={serviceType === 'charter' ? 6 : 12}>
            <Form.Item name="note" label="备注情報">
              <Input placeholder="お输入备注情報" />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ textAlign: 'right' }}>
          <Button 
            type="primary" 
            htmlType="submit"
            disabled={serviceType === 'carpool' && route.availableSeats === 0}
          >
            {serviceType === 'carpool' ? '加入拼车' : '立即包车'}
          </Button>
        </div>
      </Form>
    </Card>
  );

  return (
    <div style={{ padding: '20px 0' }}>
      {/* Demo標識 */}
      <DemoBanner />

      <div className="container">
        {/* 服务类型选择 */}
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <h2 style={{ margin: 0 }}>跨境用车服务</h2>
            <Radio.Group value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
              <Radio.Button value="carpool">相乘り (拼车)</Radio.Button>
              <Radio.Button value="charter">包车</Radio.Button>
            </Radio.Group>
          </div>

          {/* 検索表单 */}
          <Form
            form={form}
            onFinish={handleSearch}
            layout="inline"
            style={{ width: '100%' }}
          >
            <Form.Item name="fromCity" label="出发城市" rules={[{ required: true }]}>
              <Select placeholder="选择出发城市" style={{ width: 120 }}>
                {cities.map(city => (
                  <Option key={city.value} value={city.value}>{city.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item name="toCity" label="目的城市" rules={[{ required: true }]}>
              <Select placeholder="选择目的城市" style={{ width: 120 }}>
                {cities.map(city => (
                  <Option key={city.value} value={city.value}>{city.label}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                検索路线
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* 路线列表 */}
        <div className="routes-list">
          {availableRoutes.length > 0 ? (
            availableRoutes.map(route => (
              <RouteCard key={route.id} route={route} />
            ))
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                <CarOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                <p>お选择出发地和目的地検索路线</p>
              </div>
            </Card>
          )}
        </div>

        {/* 確認注文模态框 */}
        <Modal
          title="確認注文"
          open={confirmModalVisible}
          onCancel={() => setConfirmModalVisible(false)}
          width={600}
          footer={null}
        >
          {orderData && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <Tag color={serviceType === 'carpool' ? 'blue' : 'orange'}>
                  {serviceType === 'carpool' ? '相乗り (拼车)' : '包车'}
                </Tag>
              </div>

              <Steps direction="vertical" size="small" style={{ marginBottom: 20 }}>
                <Step 
                  title="出发" 
                  description={`${cities.find(c => c.value === orderData.fromCity)?.label} - ${orderData.pickupPoint}`}
                  icon={<EnvironmentOutlined />}
                />
                <Step 
                  title="到达" 
                  description={`${cities.find(c => c.value === orderData.toCity)?.label} - ${orderData.dropoffPoint}`}
                  icon={<EnvironmentOutlined />}
                />
              </Steps>

              <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 4, marginBottom: 20 }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <div><ClockCircleOutlined /> 出发時間：{dayjs(orderData.scheduledTime).format('YYYY-MM-DD HH:mm')}</div>
                  </Col>
                  <Col span={12}>
                    <div><UserOutlined /> 乘客人数：{orderData.passengerCount}人</div>
                  </Col>
                  {serviceType === 'charter' && (
                    <>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <div><CarOutlined /> 车型：{orderData.vehicleType === 'business5' ? '商务5座' : '商务7座'}</div>
                      </Col>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <div>价格倍数：{orderData.priceMultiplier}x</div>
                      </Col>
                    </>
                  )}
                  {orderData.note && (
                    <Col span={24} style={{ marginTop: 8 }}>
                      <div>备注：{orderData.note}</div>
                    </Col>
                  )}
                </Row>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderTop: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 16 }}>总计：</span>
                <span style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>¥{orderData.totalAmount}</span>
              </div>

              <div style={{ textAlign: 'right', marginTop: 20 }}>
                <Button style={{ marginRight: 8 }} onClick={() => setConfirmModalVisible(false)}>
                  キャンセル
                </Button>
                <Button type="primary" onClick={handleConfirmOrder}>
                  確認下单
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default RidePage;